import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Navigation } from 'app/core/navigation/navigation.types';
import { TasksService } from 'app/modules/admin/apps/task-list/task-list.service';
import { Observable, ReplaySubject, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { UserService } from '../user/user.service';

@Injectable({
	providedIn: 'root'
})
export class NavigationService {
	private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);
	// Navegação atual + última contagem de tarefas conhecida, para o contador do item
	// "Tasks" acompanhar a caixa de tarefas (antes ficava fixo em "0").
	private _navigationData: Navigation | null = null;
	private _lastTaskCount: number | null = null;

	/**
	 * Constructor
	 */
	constructor(
		private _httpClient: HttpClient,
		private taskListService: TasksService,
		private userService: UserService
	) {
		// Sempre que as tarefas carregarem (aqui no menu ou na tela de Tasks, inclusive
		// depois de concluir uma), o contador do menu acompanha.
		this.taskListService.tasks$.subscribe((tasks) => {
			if (tasks) {
				this._lastTaskCount = tasks.length;
				this._applyTasksBadge();
			}
		});
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Accessors
	// -----------------------------------------------------------------------------------------------------

	/**
	 * Getter for navigation
	 */
	get navigation$(): Observable<Navigation> {
		return this._navigation.asObservable();
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Public methods
	// -----------------------------------------------------------------------------------------------------

	/**
	 * Get all navigation data
	 */
	get(): Observable<Navigation> {
		// Busca as tarefas sem travar o menu; o resultado atualiza o contador pelo
		// tasks$ do construtor. `task?type=tasks` consulta o Camunda e enriquece cada
		// tarefa, então o timeout é folgado (vem do Aura: 15s cancelava com frequência).
		this.taskListService.getTasks().pipe(
			timeout(60000),
			catchError(() => of(null))
		).subscribe();

		return this._httpClient.get<Navigation>('api/common/navigation').pipe(
			map((navigation) => {
				const userPermissions = this.userService.getAllUserGroups();
				navigation.default = this.filterNavigationByPermission(navigation.default, userPermissions);
				this._navigationData = navigation;
				this._applyTasksBadge();
				this._navigation.next(navigation);
				return navigation;
			})
		);
	}

	/**
	 * Mostra no item "Tasks" quantas tarefas estão com o usuário. Sem tarefa, sem
	 * contador (um "0" permanente só vira ruído no menu).
	 */
	private _applyTasksBadge(): void {
		if (this._lastTaskCount === null || !this._navigationData) {
			return;
		}
		const tasksItem = this._navigationData.default.find((item) => item.id === 'tasks');
		if (tasksItem) {
			tasksItem.badge = this._lastTaskCount > 0
				? { title: this._lastTaskCount.toString(), classes: 'px-2.5 bg-white text-gray-900 font-semibold rounded-full' }
				: undefined;
			this._navigation.next(this._navigationData);
		}
	}

	/**
	 * Filter navigation items by permission
	 */
	private filterNavigationByPermission(items: any[], permissions: string[]): any[] {
		return items
			.filter(item => {
				if (!item.auth) return true;
				return item.auth.some(auth => permissions.includes(auth));
			})
			.map(item => ({
				...item,
				children: item.children ? this.filterNavigationByPermission(item.children, permissions) : undefined
			}));
	}
}
